// The shell's AUTH router guard — the gate before mount, on every navigation.
//
// Registered in context.js `router.guards` (smbls runs guards inside the
// router before pushState / content mount: `true` allows, a string
// redirects). The decision is functions/auth.js authRedirectTarget — the SAME
// predicate the parcel cold boot runs in redirectIfUnauthed — with THE public
// path list the boot gate reads too (shared guestRoutes.js isGuestAllowedPath,
// reached through the element so a published library function gets its own
// receiver). On the published shell (mermaid
// runs Smbls.create(app, context) and never index.js) this guard IS the auth
// gate: an anonymous visitor to a protected path lands on /login?next=… and
// the protected page never mounts. The session test is the token in storage
// (hasSessionToken) — synchronous, no SDK, so it holds before SDK init.
// REVIEW FIXES (2026-10-09, ws crm one-boot delta review):
//  · D5 — the wait for the gate is BOUNDED (context.bootGuardWaitMs, else
//    30 s). Past it the guard fails CLOSED: treated as signed out, so a
//    protected path bounces to /login?next=… and never mounts. Nothing is
//    purged — the stored session is kept, the next load signs straight in.
//  · D9 — a gate that SETTLED without a verdict (a failed boot) is signed
//    out. The stored token answers only on a host with no boot gate at all.
//  · Warm early mount (2026-10-10) — a gated host whose gate does not exist
//    yet (bootMode set, no bootReady) waits for the gate to appear, inside the
//    same bound, instead of deciding "no verdict = signed out" at once.
//  · Published shell — the router calls a guard with NO receiver, so frank's
//    `__scope.<import>` rewrite of a module-scope read resolved on `{}`
//    (`__scope.hasSessionToken is not a function` on every boot; the receiver
//    rule in smbls frank getElementRef does not see a DESTRUCTURED `element`
//    param). The guard therefore reads NO module binding: every helper is
//    reached through `element.call` (all five are registry functions), and
//    the bound is an inline literal.
export const authRouteGuard = ({ pathname, query, element }) => {
  const path = pathname || '/'
  const call = (name, ...args) =>
    element && typeof element.call === 'function' ? element.call(name, ...args) : undefined
  const isPublicPath = (p) => !!call('isGuestAllowedPath', p)
  const ctx = (element && element.context) || null
  // a boot gate exists on this host (parcel bootShell or the published plugin)
  const gated = !!(ctx && (ctx.bootReady || ctx.bootMode))
  const decide = () => {
    let authed
    if (ctx && typeof ctx.bootSignedIn === 'boolean') {
      const st = element.state && (element.state.root || element.state)
      authed =
        st && st._authReady === true && typeof st.signedIn === 'boolean' ? st.signedIn : ctx.bootSignedIn
    } else if (gated) {
      // D5 / D9: no verdict (the gate failed or never answered) — fail closed
      authed = false
    } else {
      authed = !!call('hasSessionToken')
    }
    const target = call('authRedirectTarget', {
      path,
      search: call('queryHashTail', query, '') || '',
      authed,
      demoHost: !!call('isDemoHost'),
      isPublicPath
    })
    return target || true
  }
  // A gated host with no verdict WAITS (never decides early) — on a protected
  // path only. The gate may not exist yet: the parcel warm start mounts EARLY
  // (bootResolve → mountEarly → create → router → here) on a context COPY that
  // carries bootMode but neither bootSignedIn nor bootReady; bootApply writes
  // both onto the mounted context later. So the guard watches for the gate to
  // APPEAR, then waits on it — all inside the ONE bound (D5). Settled with no
  // verdict, or past the bound → decide() fails closed (D9).
  if (ctx && gated && typeof ctx.bootSignedIn !== 'boolean' && !isPublicPath(path)) {
    const waitMs = Number(ctx.bootGuardWaitMs) > 0 ? Number(ctx.bootGuardWaitMs) : 30000
    return new Promise((resolve) => {
      let done = false
      let timer = null
      let poll = null
      const finish = () => {
        if (done) return
        done = true
        if (timer !== null) clearTimeout(timer)
        if (poll !== null) clearTimeout(poll)
        resolve(decide())
      }
      const watch = () => {
        poll = null
        if (done) return
        if (typeof ctx.bootSignedIn === 'boolean') return finish()
        if (ctx.bootReady) return void Promise.resolve(ctx.bootReady).then(finish, finish)
        poll = setTimeout(watch, 25)
      }
      timer = setTimeout(finish, waitMs)
      watch()
    })
  }
  return decide()
}
